import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Dimensions, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors, SIZES } from '../constants/theme';
import { Button } from './Button';
import { ratingDisplay } from '../services/ratingUtils';
import FichaPersona from './FichaPersona';
import { pesos } from '../utils/dinero';

interface RideOfferCardProps {
  type: 'PASSENGER_VIEW' | 'DRIVER_VIEW';
  userName: string;
  /** URL https a la selfie KYC / avatar. Cuando viene, mostramos la foto
   *  real en vez del icono genérico person → profesionalismo Uber. */
  userPhoto?: string;
  rating: string;
  /** Cantidad de calificaciones reales. 0/undefined → muestra "Nuevo". */
  ratingCount?: number | null;
  /** Rango del chofer, estampado por el servidor en la oferta. */
  nivel?: string | null;
  viajesCompletados?: number | null;
  distance: string;
  timeEst: string;
  /**
   * EL LARGO DEL VIAJE, que el chofer no veía por ningún lado.
   * `distance` es lo que hay hasta la RECOGIDA; esto es lo que mide el viaje en
   * sí. Sin las dos, no se puede saber si un viaje conviene: la tarifa
   * (geoUtils.suggestFare) solo cubre el segundo tramo, pero la gasolina se
   * gasta en los dos. Solo llega en la vista del chofer.
   */
  largoViaje?: string;
  /** La cuenta hecha: "Recogida 4.2 + viaje 6.1 = 10.3 km · RD$29 por km". */
  economia?: string;
  /**
   * El viaje quedó FUERA del radio cercano y se enseña solo porque no había
   * suficientes cerca. Se dice con todas sus letras: enseñar uno lejano sin
   * avisar es justo lo que molesta de la competencia.
   */
  lejos?: boolean;
  price: string;
  vehicleInfo?: string;
  /** Uber-style: mostrar detalles del vehículo al pasajero para que sepa
   *  qué carro esperar (color + marca + modelo) y verifique la placa. */
  vehicleBrand?: string | null;
  vehicleModel?: string | null;
  vehicleColor?: string | null;
  licensePlate?: string | null;
  /**
   * Foto del carro, la que el pasajero compara con lo que tiene delante.
   *
   * ⚠️ LA ESTAMPA EL SERVIDOR, NO EL TELÉFONO DEL CHOFER. Viene de
   * `vehicle_images/{uid}/`, una copia que hace el disparador
   * `sellarReputacionDeLaOferta` — NUNCA una URL de `kyc_documents/`, que es la
   * carpeta de la cédula. El porqué entero está en
   * functions/src/fotoVehiculo.ts: las URL de Storage llevan token y se sirven
   * sin consultar las reglas, así que una URL del KYC entregada aquí sería una
   * llave permanente e irrevocable a la carpeta privada de ese chofer.
   *
   * Sin dato es null y no se pinta hueco: nunca un carro genérico que haga creer
   * al pasajero que ese es el vehiculo.
   */
  vehiclePhotoUrl?: string | null;
  /** ¿Esta persona pasó de verdad la verificación de identidad (KYC)?
   *  NO se llama "biométrica" ni aquí ni en pantalla: la selfie la compara a
   *  mano una persona autorizada (ver terms.tsx y privacy.tsx).
   *  SIN DATO NO SE PINTA ESCUDO: `undefined` significa "no lo sé", y un sello
   *  que siempre dice que sí no informa de nada — afirma algo que nadie
   *  comprobó justo cuando alguien decide montarse en un carro con un
   *  desconocido. Quien use la tarjeta debe mandar el valor real. */
  isFaceIdVerified?: boolean;
  onAccept: () => void;
  onCounterOffer?: () => void;
  onReject: () => void;
  /**
   * La oferta de este chofer está viajando al servidor (H40, auditoría
   * 16-sep-2026). Cada toque de "Aceptar Viaje" hacía un `.add()` más y el
   * pasajero veía al mismo chofer dos veces. Mientras sea `true`, "Aceptar Viaje"
   * gira y no admite toques, y "Contraofertar" tampoco. Sin la prop, la tarjeta
   * se porta como siempre (así la usa la vista del pasajero).
   */
  enviando?: boolean;
}

export const RideOfferCard = ({
  type,
  userName,
  userPhoto,
  rating,
  ratingCount,
  distance,
  timeEst,
  largoViaje,
  economia,
  lejos,
  price,
  vehicleInfo,
  vehicleBrand,
  vehicleModel,
  vehicleColor,
  licensePlate,
  vehiclePhotoUrl,
  nivel,
  viajesCompletados,
  // SIN valor por defecto a propósito. Antes decía `= true`: aunque quien
  // usaba la tarjeta no mandara nada, se pintaba el escudo verde y el sello
  // "Biométrico". Ni omitiéndolo se apagaba. Ahora, sin dato, no se pinta.
  isFaceIdVerified,
  onAccept,
  onCounterOffer,
  onReject,
  enviando = false,
}: RideOfferCardProps) => {
  // Construimos el label del vehículo: "Blanco Toyota Corolla" con la placa
  // como badge separado. Si no llega ningún dato del vehículo, cae al
  // `vehicleInfo` legacy o a un placeholder neutro.
  const vehicleLine = [vehicleColor, vehicleBrand, vehicleModel]
    .filter((v) => !!v && String(v).trim().length > 0)
    .join(' ');
  const finalVehicleText = vehicleLine || vehicleInfo || '';
  const colors = useThemeColors();
  const [fichaVisible, setFichaVisible] = React.useState(false);

  return (
    <View style={[styles.card, { backgroundColor: colors.card, shadowColor: colors.text }]}>
      {/* En PASSENGER_VIEW la tarjeta muestra a un CHOFER (por eso lleva los
          datos del vehículo); en DRIVER_VIEW muestra al pasajero. */}
      <FichaPersona
        visible={fichaVisible}
        onCerrar={() => setFichaVisible(false)}
        datos={{
          tipo: type === 'PASSENGER_VIEW' ? 'DRIVER' : 'PASSENGER',
          nombre: userName,
          foto: userPhoto,
          calificacion: rating,
          numeroCalificaciones: ratingCount,
          verificado: isFaceIdVerified,
          marca: vehicleBrand,
          modelo: vehicleModel,
          color: vehicleColor,
          placa: licensePlate,
          vehiculo: finalVehicleText,
          fotoVehiculo: vehiclePhotoUrl,
          nivel,
          viajesCompletados,
        }}
      />
      {/* Header Profile & Price */}
      <View style={styles.headerRow}>
        <View style={styles.userInfo}>
          {/* La foto ABRE LA FICHA. Antes era un <View> pelado: el usuario lo
              tocaba esperando ver con quién iba y no pasaba nada. */}
          <TouchableOpacity
            style={{ position: 'relative', marginRight: 12 }}
            onPress={() => setFichaVisible(true)}
            accessibilityRole="button"
            accessibilityLabel="Ver los datos de esta persona"
          >
            {userPhoto && userPhoto.startsWith('http') ? (
              <Image
                source={{ uri: userPhoto }}
                style={[styles.avatar, { marginRight: 0 }]}
              />
            ) : (
              <View style={[styles.avatar, { backgroundColor: colors.background, marginRight: 0 }]}>
                <Ionicons name="person" size={24} color={colors.textSecondary} />
              </View>
            )}
            {isFaceIdVerified && (
              <View style={[styles.biometricBadgeContainer, { backgroundColor: colors.card }]}>
                <Ionicons name="shield-checkmark" size={12} color="#00C853" />
              </View>
            )}
          </TouchableOpacity>
          {/* `flex: 1` para que el nombre y el sello se ajusten al ancho que
              queda y no desplacen el precio. */}
          <View style={{ flex: 1 }}>
            {/* H74 (auditoría 16-sep-2026) — una línea. Un nombre completo real
                ("María Altagracia De Los Santos Rodríguez de Jiménez") envolvía en
                tres y empujaba los botones fuera de la hoja del mapa. El nombre
                entero sigue en la ficha que abre la foto. */}
            <Text style={[styles.userName, { color: colors.text }]} numberOfLines={1} ellipsizeMode="tail">{userName}</Text>
            <View style={styles.ratingRow}>
              {(() => {
                // "SIN DATO" Y "CERO CALIFICACIONES" NO SON LO MISMO.
                // Si `ratingCount` no llega (undefined/null), quien pinta la
                // tarjeta no nos dijo cuántas notas tiene esta persona: puede
                // ser nueva o puede llevar cien viajes. Ahí no se afirma nada
                // — ni estrellas ni "Nuevo". `0` sí es un dato real: existe y
                // todavía no la ha calificado nadie, y eso es lo que dice el
                // chip "Nuevo". Antes ambos casos caían en "Nuevo" porque el
                // ayudante trata lo que no es number como 0.
                if (ratingCount === undefined || ratingCount === null) return null;
                // Sin estrellas falsas: "Nuevo" hasta tener viajes calificados.
                const r = ratingDisplay(rating, ratingCount);
                return r.isNew ? (
                  <View style={{ backgroundColor: 'rgba(255,149,0,0.15)', paddingHorizontal: 6, paddingVertical: 1, borderRadius: 6, marginRight: 8 }}>
                    <Text style={{ fontSize: 10, fontWeight: '800', color: '#FF9500' }}>Nuevo</Text>
                  </View>
                ) : (
                  <>
                    <Ionicons name="star" size={12} color="#FFD700" />
                    <Text style={[styles.ratingText, { color: colors.textSecondary }]}>{r.label}</Text>
                  </>
                );
              })()}
              {/* ── ESTE TEXTO NO PUEDE VOLVER A DECIR "BIOMÉTRICO" ───────────
                  Decía "Biométrico" y los propios Términos de la app dicen lo
                  contrario, en negrita: la selfie de verificación "la compara a
                  mano una persona autorizada de Go Safely" y "no usamos
                  reconocimiento facial, prueba de vida ni ningún sistema
                  automático" (src/app/terms.tsx, sección de Registro y
                  Verificación; la Política de Privacidad lo repite en
                  src/app/privacy.tsx, punto "Ningún proveedor de
                  reconocimiento facial"). El proveedor de reconocimiento facial
                  se eliminó del producto: la comparación la hace una persona.
                  Mientras eso siga siendo así, aquí solo cabe lo que sí es
                  verdad — "Identidad verificada", el mismo texto que ya usa
                  FichaPersona.tsx. El icono y el color NO se tocan: son
                  identidad visual. */}
              {isFaceIdVerified && (
                <View style={[styles.verifiedBadge, { backgroundColor: '#E8F5E9', borderWidth: 1, borderColor: '#A5D6A7' }]}>
                  <Ionicons name="shield-checkmark" size={12} color="#2E7D32" style={{marginRight: 4}} />
                  <Text style={styles.verifiedText}>Identidad verificada</Text>
                </View>
              )}
            </View>
          </View>
        </View>
        
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={[styles.priceText, { color: colors.text }]}>{pesos(price)}</Text>
          <Text style={[styles.priceSub, { color: colors.textSecondary }]}>Oferta actual</Text>
        </View>
      </View>

      {/* Divider */}
      <View style={[styles.divider, { backgroundColor: colors.border }]} />

      {/* Trip Details */}
      <View style={styles.detailsContainer}>
        <View style={styles.detailItem}>
          <View style={[styles.iconBox, { backgroundColor: colors.background }]}>
            <Ionicons name="navigate" size={16} color={colors.primary} />
          </View>
          <View>
             {/* «Distancia» a secas era ambiguo: el chofer no sabía si eran los
                 km de ir a buscarlo o los del viaje. Cuando sabemos el largo
                 del viaje se nombran las dos cosas. */}
             <Text style={[styles.detailLabel, { color: colors.textSecondary }]}>
               {largoViaje ? 'Recogida' : 'Distancia'}
             </Text>
             <Text style={[styles.detailValue, { color: colors.text }]}>{distance}</Text>
          </View>
        </View>

        {!!largoViaje && (
          <View style={styles.detailItem}>
            <View style={[styles.iconBox, { backgroundColor: colors.background }]}>
              <Ionicons name="swap-horizontal" size={16} color={colors.primary} />
            </View>
            <View>
               <Text style={[styles.detailLabel, { color: colors.textSecondary }]}>Viaje</Text>
               <Text style={[styles.detailValue, { color: colors.text }]}>
                 {largoViaje.replace(' de viaje', '')}
               </Text>
            </View>
          </View>
        )}

        <View style={styles.detailItem}>
          <View style={[styles.iconBox, { backgroundColor: colors.background }]}>
            <Ionicons name="time" size={16} color={colors.accent} />
          </View>
          <View>
             <Text style={[styles.detailLabel, { color: colors.textSecondary }]}>Llegada</Text>
             <Text style={[styles.detailValue, { color: colors.text }]}>{timeEst}</Text>
          </View>
        </View>
      </View>

      {/* LA CUENTA, HECHA. Es lo que decide si acepta o no, y hasta ahora tenía
          que sacarla de cabeza mientras maneja.

          Y ADEMÁS EL SELLO DE ZONA. Antes esta franja solo se pintaba cuando
          había `economia`, y `economiaLabel` devuelve null si el viaje no trae
          coordenadas de destino o si la oferta es 0. O sea que justo en esos
          viajes el chofer no veía NINGÚN aviso de que la recogida le queda
          lejos — y es el único sitio donde se le dice. Por eso ahora la franja
          sale también con `lejos` a secas. */}
      {(!!economia || lejos) && (
        <View
          style={[
            styles.economiaRow,
            { backgroundColor: lejos ? 'rgba(255,149,0,0.12)' : colors.background },
          ]}
        >
          <Ionicons
            name={lejos ? 'alert-circle-outline' : 'calculator-outline'}
            size={15}
            color={lejos ? '#FF9500' : colors.textSecondary}
          />
          <Text
            style={[styles.economiaTexto, { color: lejos ? '#B36200' : colors.textSecondary }]}
            numberOfLines={2}
          >
            {lejos ? (economia ? `Lejos de ti · ${economia}` : 'Lejos de tu zona') : economia}
          </Text>
        </View>
      )}

      {type === 'PASSENGER_VIEW' && (finalVehicleText || licensePlate || vehiclePhotoUrl) && (
        <TouchableOpacity
          style={[styles.vehicleRow, { backgroundColor: colors.background }]}
          onPress={() => setFichaVisible(true)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Ver la ficha del chofer y la foto de su vehículo con la placa"
        >
          {/* LA FOTO DEL CARRO, si el chofer la subió en su verificación.
              Sustituye al icono genérico — no se añade al lado— para que la fila
              no crezca y la tarjeta siga cabiendo igual que antes. */}
          {vehiclePhotoUrl ? (
            <Image
              source={{ uri: vehiclePhotoUrl }}
              style={styles.fotoDelCarro}
              resizeMode="cover"
              accessibilityLabel="Foto del vehículo del chofer"
            />
          ) : (
            <Ionicons name="car-sport" size={20} color={colors.textSecondary} style={{marginRight: 8}} />
          )}
          <View style={{ flex: 1 }}>
            {finalVehicleText ? (
              // H74 — color + marca + modelo son texto libre: una línea. El
              // detalle completo está en la ficha del chofer.
              <Text style={[styles.vehicleText, { color: colors.text, fontWeight: '700' }]} numberOfLines={1} ellipsizeMode="tail">
                {finalVehicleText}
              </Text>
            ) : null}
            {licensePlate ? (
              <View style={styles.plateBadgeInline}>
                <Text style={styles.plateBadgeText}>{licensePlate}</Text>
              </View>
            ) : null}
          </View>
          {/* Se dice que esto se toca: sin la flecha, un bloque que se abre
              parece un bloque que solo informa — el fundador lo tocó y creyó
              que estaba roto. */}
          <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
        </TouchableOpacity>
      )}

      {/* Action Buttons */}
      <View style={styles.actionsContainer}>
        <Button
          title="Aceptar Viaje"
          onPress={onAccept}
          style={styles.btnAccept}
          // H40 — `loading` ya existía en Button: pinta el indicador y lo
          // deshabilita. Solo se enciende mientras la oferta viaja.
          loading={enviando}
        />
        <View style={styles.secondaryActions}>
          {/* `onCounterOffer` es opcional, y antes el botón se pintaba igual
              cuando no venía: quedaba un "Contraofertar" que al tocarlo no
              hacía nada. Eso es exactamente lo que Google Play marcó como
              "Unresponsive UI elements". Si no hay acción, no hay botón. */}
          {onCounterOffer && (
            <TouchableOpacity
              style={[styles.btnCounter, { borderColor: colors.border }]}
              onPress={onCounterOffer}
              disabled={enviando}
              accessibilityState={{ disabled: enviando }}
            >
              <Text style={[styles.btnCounterText, { color: colors.text }]}>Contraofertar</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity style={styles.btnReject} onPress={onReject}>
            <Ionicons name="close" size={24} color={colors.error} />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 24,
    padding: 20,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 8,
    marginBottom: 20,
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.02)',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    // "Identidad verificada" ocupa mas ancho que "Biometrico". Sin `flex: 1`
    // aqui y sin `flexWrap` abajo, el sello empujaba el precio fuera de la
    // tarjeta en pantallas estrechas.
    flex: 1,
    minWidth: 0,
    marginRight: 8,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  userName: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: 4,
  },
  ratingText: {
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 4,
    marginRight: 8,
  },
  verifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  verifiedText: {
    fontSize: 10,
    color: '#2E7D32',
    fontWeight: '800',
    letterSpacing: 0.2,
  },
  priceText: {
    fontSize: 24,
    fontWeight: '900',
    letterSpacing: -0.5,
  },
  priceSub: {
    fontSize: 11,
    fontWeight: '500',
    marginTop: 2,
  },
  divider: {
    height: 1,
    width: '100%',
    marginBottom: 16,
    opacity: 0.6,
  },
  detailsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingHorizontal: 8,
    // Con tres columnas (recogida · viaje · llegada) en pantalla estrecha se
    // apretaban unas contra otras. Que bajen de línea antes que solaparse.
    flexWrap: 'wrap',
    rowGap: 10,
  },
  economiaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    marginBottom: 14,
  },
  economiaTexto: {
    flex: 1,
    fontSize: 12.5,
    fontWeight: '600',
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconBox: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  detailLabel: {
    fontSize: 12,
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 15,
    fontWeight: '700',
  },
  vehicleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  vehicleText: {
    fontSize: 14,
    fontWeight: '500',
  },
  fotoDelCarro: {
    width: 56,
    height: 42,
    borderRadius: 8,
    marginRight: 10,
    backgroundColor: '#E1E8ED',
  },
  plateBadgeInline: {
    marginTop: 4,
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#0A2540',
    backgroundColor: '#FFF9C4',
  },
  plateBadgeText: {
    fontSize: 12,
    fontWeight: '900',
    color: '#0A2540',
    letterSpacing: 1.5,
  },
  actionsContainer: {
    gap: 12,
  },
  btnAccept: {
    width: '100%',
    borderRadius: 16,
    height: 54,
  },
  secondaryActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  btnCounter: {
    flex: 1,
    height: 50,
    borderWidth: 1,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnCounterText: {
    fontSize: 15,
    fontWeight: '600',
  },
  btnReject: {
    width: 50,
    height: 50,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 59, 48, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  biometricBadgeContainer: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: '#FFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1,
    elevation: 2,
    backgroundColor: '#E8F5E9',
  },
});
